import { Link } from 'react-router-dom';
import { Week, WeekStage, STAGE_LABELS, WeekStatus } from '../../types';
import { Badge } from '../ui/Badge';
import { clsx } from 'clsx';

const STAGE_ORDER: WeekStage[] = [
  'topic_selection',
  'outline',
  'content_generation',
  'graphics',
  'ready',
  'published',
];

interface PipelineBoardProps {
  weeks: Week[];
}

function statusVariant(status: WeekStatus): 'warning' | 'success' | 'error' | 'info' {
  if (status === 'published') return 'success';
  if (status === 'needs_revision') return 'error';
  if (status === 'pending_approval') return 'warning';
  return 'info';
}

function formatDate(ts: { seconds: number } | null): string {
  if (!ts) return '';
  return new Date(ts.seconds * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

export function PipelineBoard({ weeks }: PipelineBoardProps) {
  if (weeks.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400 font-body">
        No weeks yet. Start your first week to begin.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <div className="flex gap-4 min-w-max pb-2">
        {STAGE_ORDER.map((stage) => {
          const stageWeeks = weeks.filter((w) => w.stage === stage);
          return (
            <div key={stage} className="w-56 flex-shrink-0">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide font-body">
                  {STAGE_LABELS[stage]}
                </span>
                {stageWeeks.length > 0 && (
                  <span className="text-xs text-gray-400">{stageWeeks.length}</span>
                )}
              </div>
              <div className="space-y-3">
                {stageWeeks.map((week) => (
                  <Link
                    key={week.id}
                    to={`/week/${week.id}`}
                    className={clsx(
                      'block bg-white border border-gray-200 rounded-lg p-3 shadow-sm hover:shadow-md hover:border-falcon-blue transition-all'
                    )}
                  >
                    <p className="text-sm font-medium text-falcon-navy font-body truncate mb-2">
                      {week.topic?.title ?? 'Topic pending'}
                    </p>
                    <div className="flex items-center justify-between">
                      <Badge variant={statusVariant(week.status)}>
                        {week.status.replace('_', ' ')}
                      </Badge>
                      <span className="text-xs text-gray-400">
                        {formatDate(week.updated_at)}
                      </span>
                    </div>
                  </Link>
                ))}
                {stageWeeks.length === 0 && (
                  <div className="border-2 border-dashed border-gray-200 rounded-lg h-16" />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
